import {
  CompositeFilterDescriptor,
  FilterDescriptor,
} from '@progress/kendo-data-query';
import { externalFilter } from './externalFilter';

const isCompositeFilter = (
  filter: CompositeFilterDescriptor | FilterDescriptor | null | undefined
): filter is CompositeFilterDescriptor =>
  filter != null && 'filters' in filter && Array.isArray(filter.filters);

export const getAttributesFilterKey = (
  filter: CompositeFilterDescriptor | undefined
): string => {
  if (!filter?.filters?.length) {
    return '';
  }

  for (const filterItem of filter.filters) {
    if (!filterItem) {
      continue;
    }

    if (isCompositeFilter(filterItem)) {
      const nestedKey = getAttributesFilterKey(filterItem);
      if (nestedKey) {
        return nestedKey;
      }
      continue;
    }

    if (filterItem.field !== 'attributes') {
      continue;
    }

    const value = String(filterItem.value ?? '').trim();
    if (!value) {
      return '';
    }

    const operator = filterItem.operator === 'and' ? 'and' : 'or';
    return `${operator}:${value}`;
  }

  return '';
};

export const EMPTY_GRID_FILTER: CompositeFilterDescriptor = { logic: 'and', filters: [] };

export const shouldResetItemFilterForAttributes = (
  previousKey: string,
  nextKey: string
): boolean => Boolean(nextKey) && nextKey !== previousKey;

export const shouldClearAttributesForContractedItems = (
  itemFilterValue: number,
  attributesFilterKey: string
): boolean => itemFilterValue === 1 && Boolean(attributesFilterKey);

export const removeFieldFromFilter = (
  filter: CompositeFilterDescriptor | undefined,
  field: string
): CompositeFilterDescriptor | undefined => {
  if (!filter?.filters?.length) {
    return undefined;
  }

  const filters = filter.filters
    .map((filterItem) => {
      if (!filterItem) {
        return null;
      }

      if (isCompositeFilter(filterItem)) {
        return removeFieldFromFilter(filterItem, field) ?? null;
      }

      if (filterItem.field === field) {
        return null;
      }

      return filterItem;
    })
    .filter((filterItem): filterItem is CompositeFilterDescriptor | FilterDescriptor =>
      filterItem != null
    );

  if (filters.length === 0) {
    return undefined;
  }

  return { ...filter, filters };
};

export const buildItemFilterGridFilter = (
  itemFilterValue: number
): CompositeFilterDescriptor => ({
  logic: 'and',
  filters: itemFilterValue === 0 ? [] : (externalFilter[itemFilterValue]?.filters ?? []),
});

export const clearContractedItemGridFilter = (
  filter: CompositeFilterDescriptor | undefined
): CompositeFilterDescriptor => removeFieldFromFilter(filter, 'isContracted') ?? EMPTY_GRID_FILTER;

export const applyItemFilterToGridFilter = (
  filter: CompositeFilterDescriptor | undefined,
  itemFilterValue: number
): CompositeFilterDescriptor => {
  const withoutItemFilter = clearContractedItemGridFilter(filter);

  if (itemFilterValue === 0) {
    return withoutItemFilter;
  }

  const itemFilterDescriptors = externalFilter[itemFilterValue]?.filters ?? [];
  if (itemFilterDescriptors.length === 0) {
    return withoutItemFilter;
  }

  return {
    logic: 'and',
    filters: [...(withoutItemFilter.filters ?? []), ...itemFilterDescriptors],
  };
};
